/**
 * Compact Google Reviews social proof badge.
 *
 * Renders the Google "G" mark, the "Google Reviews" label, a 5-star rating row,
 * and the firm's review summary. Brand-compliant (paper bg, ink text, teal
 * accents). Designed to slot between two existing landing-page sections —
 * works well above Testimonials as a credibility lead-in.
 *
 * Source of truth: Google Business Profile for Jos Family Law.
 *   Rating:  4.7 / 5
 *   Reviews: 175+
 */
export default function GoogleReviews({
  rating = 4.7,
  count = "175+",
  hrefToReviews = "https://www.google.com/maps/place/Jos+Family+Law",
} = {}) {
  return (
    <section
      data-testid="google-reviews-badge"
      aria-label="Google client reviews"
      className="relative bg-paper border-y border-ink/10"
    >
      <div className="max-w-[1320px] mx-auto px-5 md:px-12 lg:px-20 py-8 md:py-10">
        <a
          href={hrefToReviews}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="google-reviews-link"
          className="group inline-flex flex-col md:flex-row md:items-center gap-4 md:gap-7 mx-auto md:mx-0 text-center md:text-left"
        >
          {/* Google G mark */}
          <span className="inline-flex items-center justify-center shrink-0 w-12 h-12 md:w-14 md:h-14 bg-paper border border-gray-300/70 group-hover:border-ink transition-colors">
            <svg viewBox="0 0 48 48" width="26" height="26" aria-hidden="true">
              <path
                fill="#EA4335"
                d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
              />
              <path
                fill="#4285F4"
                d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
              />
              <path
                fill="#FBBC05"
                d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
              />
              <path
                fill="#34A853"
                d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
              />
            </svg>
          </span>

          {/* Label + stars + summary */}
          <div className="flex flex-col gap-2 md:gap-1.5">
            <div className="flex items-center justify-center md:justify-start gap-3">
              <p className="font-display italic text-[22px] md:text-[26px] leading-none tracking-tight text-ink">
                Google Reviews
              </p>
              <div className="flex items-center gap-0.5" aria-hidden="true">
                {[0, 1, 2, 3, 4].map((i) => (
                  <svg
                    key={i}
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    className="fill-teal"
                  >
                    <path d="M12 2.5l2.93 6.94 7.57.6-5.76 4.98 1.78 7.34L12 18.27l-6.52 4.09 1.78-7.34L1.5 10.04l7.57-.6L12 2.5z" />
                  </svg>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap items-baseline justify-center md:justify-start gap-3">
              <p className="font-display text-[34px] md:text-[42px] leading-none tracking-tight text-teal">
                {rating}
                <span className="text-ink/70">/</span>
                <span className="text-ink/70">5</span>
              </p>
              <p className="font-mono text-[10px] md:text-[11px] tracking-[0.24em] uppercase text-gray-700">
                <span className="hidden md:inline text-gray-400 mr-3">|</span>
                {count} Client Reviews
              </p>
            </div>
            <p className="font-mono text-[9px] md:text-[10px] tracking-[0.24em] uppercase text-gray-500 group-hover:text-ink transition-colors">
              Read on Google &nbsp;→
            </p>
          </div>
        </a>
      </div>
    </section>
  );
}
